import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";
import { API_BASE_URL, clearAuthStorage } from "api/axiosConfig";

const AuthContext = createContext(null);

const LS_TOKEN = "authToken";
const LS_USER = "authUser";
const LS_EMAIL = "userEmail";

function safeJsonParse(value) {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function readStoredSession() {
  const token = localStorage.getItem(LS_TOKEN);
  const user = safeJsonParse(localStorage.getItem(LS_USER));

  if (token && user) {
    if (user?.email) {
      localStorage.setItem(LS_EMAIL, user.email);
    }

    return { token, user };
  }

  return null;
}

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(() => readStoredSession());
  const [loading] = useState(false);

  const syncSessionFromStorage = () => {
    setSession(readStoredSession());
  };

  useEffect(() => {
    const handleStorage = (event) => {
      if (
        event.key === LS_TOKEN ||
        event.key === LS_USER ||
        event.key === LS_EMAIL
      ) {
        syncSessionFromStorage();
      }
    };

    const handleLogoutEvent = () => {
      setSession(null);
    };

    const handleSessionExpired = () => {
      setSession(null);
    };

    window.addEventListener("storage", handleStorage);
    window.addEventListener("auth:logout", handleLogoutEvent);
    window.addEventListener("auth:session-expired", handleSessionExpired);

    const intervalId = window.setInterval(() => {
      const token = localStorage.getItem(LS_TOKEN);
      const user = localStorage.getItem(LS_USER);

      setSession((currentSession) => {
        if (currentSession && (!token || !user)) {
          window.dispatchEvent(new CustomEvent("auth:logout"));
          return null;
        }

        return currentSession;
      });
    }, 1500);

    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("auth:logout", handleLogoutEvent);
      window.removeEventListener("auth:session-expired", handleSessionExpired);
      window.clearInterval(intervalId);
    };
  }, []);

  const signUp = async (name, email, password) => {
    try {
      const res = await axios.post(`${API_BASE_URL}/api/auth/signup`, {
        name,
        email,
        password,
      });

      return {
        success: true,
        data: res.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error?.response?.data || {
          message: error.message || "Signup failed",
        },
        status: error?.response?.status,
      };
    }
  };

  const signIn = async (email, password) => {
    try {
      const res = await axios.post(`${API_BASE_URL}/api/auth/login`, {
        email,
        password,
      });

      const token = res.data?.token;
      const user = res.data?.user;

      if (!token || !user) {
        return {
          success: false,
          error: {
            message: "Login response missing token/user",
          },
        };
      }

      localStorage.setItem(LS_TOKEN, token);
      localStorage.setItem(LS_USER, JSON.stringify(user));
      localStorage.setItem(LS_EMAIL, user.email || email);

      const nextSession = { token, user };
      setSession(nextSession);

      return {
        success: true,
        data: res.data,
        accessToken: token,
      };
    } catch (error) {
      return {
        success: false,
        error: error?.response?.data || {
          message: error.message || "Login failed",
        },
        status: error?.response?.status,
      };
    }
  };

  const signInWithGoogle = async () => {
    return {
      success: false,
      error: {
        message: "Google OAuth is currently disabled.",
      },
    };
  };

  const signOut = async () => {
    clearAuthStorage();
    setSession(null);
    window.dispatchEvent(new CustomEvent("auth:logout"));
  };

  const value = useMemo(
    () => ({
      session,
      user: session?.user || null,
      token: session?.token || null,
      signUp,
      signIn,
      signInWithGoogle,
      signOut,
      loading,
      isAuthenticated: !!session?.token,
    }),
    [session, loading]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
